// utils/chatPromptGenerator.js

import { AI_Entity, Entity_Label, ModulePath } from "../constant/ApplicationVariables";

/**
 * Generate default chat input based on current context
 * @param {Object} params - Parameters for generating the prompt
 * @returns {string} - Generated prompt text
 */
export const generateChatPrompt = ({
    isItemChatWindow,
    currentUserStoryData,
    currentUseCaseData,
    pathName,
    currentFolderData,
    currentAIContextData,
    currentAIEntityData,
    activeProject,
    userInput
}) => {
    let inputValue = "";

    if (isItemChatWindow) {
        inputValue = generateItemChatWindowPrompt({
            pathName,
            currentUserStoryData,
            currentUseCaseData,
            currentAIEntityData
        });
    } else if (currentAIEntityData?.entity === AI_Entity.folder) {
        inputValue = generateFolderPrompt(activeProject);
    } else if (currentFolderData) {
        inputValue = generateFolderContextPrompt({
            currentFolderData,
            currentAIContextData,
            currentAIEntityData,
            pathName,
            activeProject
        });
    }

    // Only return if we have a valid input and it's different from current
    if (inputValue && inputValue !== userInput) {
        return inputValue;
    }
    
    return null;
};

/**
 * Generate prompt for item chat window (User Story / Use Case context)
 */
const generateItemChatWindowPrompt = ({
    pathName,
    currentUserStoryData,
    currentUseCaseData,
    currentAIEntityData
}) => {
    const currentData = pathName === ModulePath.requirements
        ? currentUserStoryData
        : currentUseCaseData;

    if (!currentData) return "";

    let prefixText = "";
    let contextDescription = "";

    if (pathName === ModulePath.requirements) {
        return generateUserStoryBasedPrompt(currentData, currentAIEntityData);
    } else if (pathName === ModulePath.useCase) {
        return generateUseCaseBasedPrompt(currentData, currentAIEntityData);
    }

    return "";
};

/**
 * Generate prompt based on User Story context
 */
const generateUserStoryBasedPrompt = (userStoryData, aiEntityData) => {
    const userStoryTitle = userStoryData.title || userStoryData.name || "";
    const userStoryDescription = userStoryData.description || "";
    const acceptanceCriteria = userStoryData.acceptanceCriteria || userStoryData.acceptance_criteria || [];
    const acceptanceText = Array.isArray(acceptanceCriteria) 
        ? acceptanceCriteria.join(", ") 
        : acceptanceCriteria;

    let prefixText = "";
    let contextDescription = "";

    if (aiEntityData?.entity === AI_Entity.useCase) {
        prefixText = `Based on the User Story "${userStoryTitle}", generate comprehensive Use Cases that cover all functional scenarios, including:`;
        contextDescription = `
            User Story: ${userStoryTitle}
            Description: ${userStoryDescription}
            Acceptance Criteria: ${acceptanceText}
            
            Please generate complete Use Cases with:
            1. Main Success Scenario (Happy Path)
            2. Alternative Flows
            3. Exception Flows
            4. Pre-conditions and Post-conditions
            5. Business Rules and Constraints
        `;
    } else if (aiEntityData?.entity === AI_Entity.testCase) {
        prefixText = `Based on the User Story "${userStoryTitle}", generate comprehensive Test Cases that ensure complete coverage, including:`;
        contextDescription = `
            User Story: ${userStoryTitle}
            Description: ${userStoryDescription}
            Acceptance Criteria: ${acceptanceText}
            
            Please generate detailed Test Cases covering:
            1. Positive Test Cases (Happy Path)
            2. Negative Test Cases (Error Handling)
            3. Boundary Value Analysis
            4. Edge Cases
            5. Integration Test Scenarios
            6. Performance and Security Test Considerations
        `;
    } else if (aiEntityData?.entity === AI_Entity.folder) {
        prefixText = `Based on the User Story "${userStoryTitle}", generate a comprehensive Folder Structure that organizes all related artifacts:`;
        contextDescription = `
            User Story: ${userStoryTitle}
            Description: ${userStoryDescription}
            Acceptance Criteria: ${acceptanceText}
            
            Please create a hierarchical folder structure that includes:
            1. Main feature/module folders
            2. Sub-feature breakdown
            3. Supporting components
            4. Documentation structure
            5. Test organization
            6. Configuration and resources
        `;
    } else {
        // Default for User Story generation
        prefixText = `Generate a comprehensive User Story for the following requirement:`;
        contextDescription = `
            Context: ${userStoryData.context || userStoryData.description || ""}
            Requirements: ${userStoryData.requirements || userStoryData.description || ""}
            
            Please include:
            1. User Story Title and ID
            2. As a [role], I want [goal] so that [benefit]
            3. Detailed Description
            4. Acceptance Criteria (Given-When-Then format)
            5. Business Rules
            6. Technical Notes
            7. Dependencies
            8. Priority and Effort Estimation
        `;
    }

    return `${prefixText}\n\n${contextDescription}`.trim();
};

/**
 * Generate prompt based on Use Case context
 */
const generateUseCaseBasedPrompt = (useCaseData, aiEntityData) => {
    const useCaseTitle = useCaseData.title || useCaseData.name || "";
    const useCaseDescription = useCaseData.description || "";
    const useCaseSteps = useCaseData.useCaseSteps || useCaseData.steps || [];
    const stepsText = Array.isArray(useCaseSteps) 
        ? useCaseSteps.map((step, i) => `${i+1}. ${step}`).join("\n")
        : useCaseSteps;

    let prefixText = "";
    let contextDescription = "";

    if (aiEntityData?.entity === AI_Entity.userStory) {
        prefixText = `Based on the Use Case "${useCaseTitle}", generate detailed User Stories that capture all functional requirements:`;
        contextDescription = `
            Use Case: ${useCaseTitle}
            Description: ${useCaseDescription}
            Steps: \n${stepsText}
            
            Please generate User Stories that cover:
            1. Primary Actor Goals
            2. Functional Requirements
            3. Non-Functional Requirements
            4. Business Rules
            5. User Interface Requirements
            6. Data Requirements
            7. Integration Points
            8. Security and Compliance Considerations
        `;
    } else if (aiEntityData?.entity === AI_Entity.testCase) {
        prefixText = `Based on the Use Case "${useCaseTitle}", generate comprehensive Test Cases that validate all scenarios:`;
        contextDescription = `
            Use Case: ${useCaseTitle}
            Description: ${useCaseDescription}
            Steps: \n${stepsText}
            
            Please generate Test Cases for:
            1. Main Success Scenario Testing
            2. Alternative Flow Testing
            3. Exception Handling Testing
            4. Data Validation Testing
            5. Boundary and Edge Case Testing
            6. Integration Testing
            7. Usability Testing
            8. Performance and Load Testing
        `;
    } else if (aiEntityData?.entity === AI_Entity.folder) {
        prefixText = `Based on the Use Case "${useCaseTitle}", generate a comprehensive Folder Structure for organizing all related artifacts:`;
        contextDescription = `
            Use Case: ${useCaseTitle}
            Description: ${useCaseDescription}
            Steps: \n${stepsText}
            
            Please create a detailed folder structure that includes:
            1. Core functionality folders
            2. Supporting modules
            3. Data models and schemas
            4. API specifications
            5. User interface components
            6. Testing artifacts
            7. Documentation
            8. Configuration and deployment
        `;
    } else {
        // Default for Use Case generation
        prefixText = `Generate a comprehensive Use Case for the following:`;
        contextDescription = `
            Context: ${useCaseData.context || useCaseData.description || ""}
            Requirements: ${useCaseData.requirements || useCaseData.description || ""}
            
            Please include:
            1. Use Case Name and ID
            2. Actors (Primary and Secondary)
            3. Pre-conditions
            4. Main Success Scenario (Step-by-Step)
            5. Alternative Flows
            6. Exception Flows
            7. Post-conditions
            8. Business Rules
            9. Performance Requirements
            10. Security Considerations
        `;
    }

    return `${prefixText}\n\n${contextDescription}`.trim();
};

/**
 * Generate prompt for Folder entity
 */
const generateFolderPrompt = (activeProject) => {
    return `Generate a comprehensive folder structure for the project "${activeProject?.name}" with the following considerations:
        
        Project: ${activeProject?.name}
        ${activeProject?.description ? `Description: ${activeProject?.description}` : ''}
        ${activeProject?.type ? `Project Type: ${activeProject?.type}` : ''}
        ${activeProject?.industry ? `Industry: ${activeProject?.industry}` : ''}
        
        Please create a well-organized folder structure that includes:
        1. Main Modules/Folders
        2. Sub-modules and components
        3. Documentation structure
        4. Testing and QA folders
        5. Configuration and deployment
        6. Resources and assets
        7. Database and data models
        8. API and integration layers
        9. Security and compliance
        10. DevOps and infrastructure
        
        Ensure the structure follows best practices and is scalable for future growth.`;
};

/**
 * Generate prompt based on Folder context
 */
const generateFolderContextPrompt = ({
    currentFolderData,
    currentAIContextData,
    currentAIEntityData,
    pathName,
    activeProject
}) => {
    const entityType = currentAIEntityData?.entity || 
                      (pathName === ModulePath.requirements ? AI_Entity.userStory : 
                       pathName === ModulePath.useCase ? AI_Entity.useCase : 
                       pathName === ModulePath.testCase ? AI_Entity.testCase : 
                       AI_Entity.userStory);
    
    const folderName = currentFolderData.name || "";
    const folderDescription = currentFolderData.description || "";
    const folderPath = currentFolderData.path || "";
    
    // Build context about the folder
    const folderContext = `
        Folder: ${folderName}
        ${folderDescription ? `Description: ${folderDescription}` : ''}
        ${folderPath ? `Path: ${folderPath}` : ''}
        ${currentAIContextData?.section ? `Section: ${currentAIContextData.section}` : ''}
        ${currentAIContextData?.parent ? `Parent: ${currentAIContextData.parent}` : ''}
        ${activeProject?.name ? `Project: ${activeProject.name}` : ''}
    `;

    let prefixText = "";
    let inputValue = "";

    switch (entityType) {
        case AI_Entity.userStory:
            prefixText = `Generate comprehensive User Stories for the folder "${folderName}" with the following context:`;
            inputValue = `${prefixText}
                
                ${folderContext}
                
                Please generate detailed User Stories that include:
                1. Epic/Feature Level User Stories
                2. Detailed User Stories (As a [role], I want [goal] so that [benefit])
                3. Acceptance Criteria (Given-When-Then format)
                4. Functional Requirements
                5. Non-Functional Requirements
                6. User Interface Requirements
                7. Data Requirements
                8. Integration Points
                9. Security Requirements
                10. Performance Requirements
                
                Ensure comprehensive coverage of all functionality.`;
            break;

        case AI_Entity.useCase:
            prefixText = `Generate comprehensive Use Cases for the folder "${folderName}" with the following context:`;
            inputValue = `${prefixText}
                
                ${folderContext}
                
                Please generate detailed Use Cases that include:
                1. Primary and Secondary Actors
                2. Pre-conditions and Post-conditions
                3. Main Success Scenario (Step-by-Step)
                4. Alternative Flows
                5. Exception Flows
                6. Business Rules and Constraints
                7. Functional Requirements
                8. Data Validation Rules
                9. Security and Access Control
                10. Performance Considerations
                
                Ensure complete coverage of all business scenarios.`;
            break;

        case AI_Entity.testCase:
            prefixText = `Generate comprehensive Test Cases for the folder "${folderName}" with the following context:`;
            inputValue = `${prefixText}
                
                ${folderContext}
                
                Please generate detailed Test Cases that include:
                1. Test Case ID and Title
                2. Pre-conditions
                3. Test Steps (Step-by-Step)
                4. Expected Results
                5. Positive Test Cases
                6. Negative Test Cases
                7. Boundary Value Analysis
                8. Edge Cases
                9. Integration Test Scenarios
                10. Regression Test Scenarios
                11. Performance and Load Testing
                12. Security Testing
                13. Usability Testing
                14. Test Data Requirements
                
                Ensure comprehensive test coverage for all functionality.`;
            break;

        case AI_Entity.folder:
            prefixText = `Generate a comprehensive sub-folder structure for "${folderName}" with the following context:`;
            inputValue = `${prefixText}
                
                ${folderContext}
                
                Please create a detailed hierarchical folder structure that includes:
                1. Main component folders
                2. Sub-component breakdown
                3. Shared/Common folders
                4. Configuration folders
                5. Resource folders
                6. Test folders
                7. Documentation folders
                8. Integration folders
                9. Security folders
                10. Deployment folders
                
                Ensure the structure follows industry best practices and is scalable.`;
            break;

        default:
            return "";
    }

    return inputValue.trim();
};

/**
 * Generate a quick prompt for specific scenarios
 * @param {string} type - Type of prompt to generate
 * @param {Object} data - Data for the prompt
 * @returns {string} - Generated prompt
 */
export const generateQuickPrompt = (type, data) => {
    const prompts = {
        'userStory': () => {
            const { title, description, folderName } = data;
            return `Generate a User Story for "${title || folderName || 'the feature'}":
            
                ${description ? `Description: ${description}` : ''}
                
                Include:
                1. User Story (As a... I want... So that...)
                2. Acceptance Criteria (Given-When-Then)
                3. Functional Requirements
                4. Non-Functional Requirements
                5. UI/UX Considerations
                6. Data Requirements
                7. Integration Points`;
        },
        
        'useCase': () => {
            const { title, description, folderName } = data;
            return `Generate Use Cases for "${title || folderName || 'the feature'}":
            
                ${description ? `Description: ${description}` : ''}
                
                Include:
                1. Primary and Secondary Actors
                2. Main Success Scenario
                3. Alternative Flows
                4. Exception Flows
                5. Pre and Post Conditions
                6. Business Rules
                7. Security Requirements`;
        },
        
        'testCase': () => {
            const { title, description, folderName } = data;
            return `Generate Test Cases for "${title || folderName || 'the feature'}":
            
                ${description ? `Description: ${description}` : ''}
                
                Include:
                1. Positive Test Cases
                2. Negative Test Cases
                3. Boundary Value Analysis
                4. Edge Cases
                5. Integration Test Scenarios
                6. Performance Test Scenarios
                7. Security Test Scenarios`;
        },
        
        'folder': () => {
            const { name, description, projectName } = data;
            return `Generate a folder structure for "${name || projectName || 'the project'}":
            
                ${description ? `Description: ${description}` : ''}
                
                Include:
                1. Main Module Folders
                2. Sub-component Folders
                3. Resource Folders
                4. Configuration Folders
                5. Test Folders
                6. Documentation Folders
                7. Deployment Folders`;
        }
    };

    return prompts[type] ? prompts[type]() : "";
};

/**
 * Add additional context to an existing prompt
 * @param {string} existingPrompt - The existing prompt
 * @param {Object} additionalContext - Additional context to add
 * @returns {string} - Enhanced prompt
 */
export const enhancePromptWithContext = (existingPrompt, additionalContext) => {
    if (!existingPrompt || !additionalContext) return existingPrompt;

    const contextStr = Object.entries(additionalContext)
        .filter(([_, value]) => value)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');

    return `
        ${existingPrompt}
        
        Additional Context:
        ${contextStr}
        
        Please incorporate this context into your response.
    `.trim();
};